import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { usePriceType } from './usePriceType';
import { useAuth } from '../contexts/AuthContext';

export type PriceType = 'publico' | 'contado' | 'credito' | 'mayorista';

export interface ProductPriceData {
  price: number;
  offerPrice: number | null;
  offerDays: number | null;
  discountPercentage: number | null;
  isOnSale: boolean;
  minQuantity: number;
}

export interface ProductPricesMap {
  [productId: string]: ProductPriceData;
}

interface UseProductPricesOptions {
  enabled?: boolean;
}

interface UseProductPricesReturn {
  prices: ProductPricesMap;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  getPrice: (productId: string) => ProductPriceData | null;
  getDisplayPrice: (productId: string) => number;
  getOriginalPrice: (productId: string) => number | null;
  isOnSale: (productId: string) => boolean;
  getDiscountPercentage: (productId: string) => number | null;
}

// Cache global para precios (evita re-fetches innecesarios)
const priceCache = new Map<string, { data: ProductPricesMap; timestamp: number }>();
const CACHE_TTL = 60000; // 1 minuto

/**
 * Hook centralizado para obtener precios de productos.
 * Reemplaza las 11 implementaciones duplicadas de fetchProductPrices/fetchPrices.
 *
 * @param productIds - Array de IDs de productos
 * @param options - Opciones adicionales
 * @returns Objeto con precios, loading, error y funciones helper
 *
 * @example
 * const { prices, loading, getDisplayPrice, isOnSale } = useProductPrices(productIds);
 *
 * // Obtener precio para mostrar
 * const price = getDisplayPrice(product.id);
 *
 * // Verificar si está en oferta
 * if (isOnSale(product.id)) {
 *   const discount = getDiscountPercentage(product.id);
 * }
 */
export function useProductPrices(
  productIds: string[],
  options: UseProductPricesOptions = {}
): UseProductPricesReturn {
  const { enabled = true } = options;
  const { user } = useAuth();
  const { priceType } = usePriceType();
  const [prices, setPrices] = useState<ProductPricesMap>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref para evitar fetch duplicados
  const fetchingRef = useRef(false);
  const lastFetchKey = useRef<string>('');

  // Determinar el tipo de precio efectivo
  const effectivePriceType: PriceType = (user && priceType)
    ? (priceType as PriceType)
    : 'publico';

  const fetchPrices = useCallback(async () => {
    if (!enabled || productIds.length === 0 || fetchingRef.current) {
      return;
    }

    // Crear clave única para esta consulta
    const sortedIds = [...productIds].sort();
    const fetchKey = `${sortedIds.join(',')}:${effectivePriceType}`;

    // Evitar fetch duplicado para la misma consulta
    if (fetchKey === lastFetchKey.current && Object.keys(prices).length > 0) {
      return;
    }

    // Verificar cache
    const cached = priceCache.get(fetchKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setPrices(cached.data);
      return;
    }

    try {
      fetchingRef.current = true;
      setLoading(true);
      setError(null);

      const { data: priceRows, error: pricesError } = await supabase
        .from('product_prices')
        .select('product_id, price_type, min_quantity, price, offer_price, offer_days')
        .in('product_id', productIds)
        .eq('price_type', effectivePriceType);

      if (pricesError) throw pricesError;

      const pricesMap: ProductPricesMap = {};

      if (priceRows && priceRows.length > 0) {
        // Agrupar por product_id
        const grouped: Record<string, typeof priceRows> = {};
        priceRows.forEach(row => {
          (grouped[row.product_id] ||= []).push(row);
        });

        // Procesar cada producto
        Object.entries(grouped).forEach(([productId, rows]) => {
          // Seleccionar la fila con menor min_quantity (precio base)
          const sortedRows = rows.sort((a, b) => a.min_quantity - b.min_quantity);
          const baseRow = sortedRows[0];

          const hasOffer = baseRow.offer_price != null && baseRow.offer_price > 0;
          const discountPercentage = hasOffer
            ? Math.round(((baseRow.price - baseRow.offer_price!) / baseRow.price) * 100)
            : null;

          pricesMap[productId] = {
            price: baseRow.price,
            offerPrice: baseRow.offer_price ?? null,
            offerDays: baseRow.offer_days ?? null,
            discountPercentage,
            isOnSale: hasOffer,
            minQuantity: baseRow.min_quantity
          };
        });
      }

      // Guardar en cache
      priceCache.set(fetchKey, { data: pricesMap, timestamp: Date.now() });
      lastFetchKey.current = fetchKey;

      setPrices(pricesMap);
    } catch (err) {
      console.error('Error fetching product prices:', err);
      setError(err instanceof Error ? err.message : 'Error al obtener precios');
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [productIds.join(','), effectivePriceType, enabled]);

  // Fetch inicial y cuando cambian dependencias
  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  // Helper: obtener datos de precio completos
  const getPrice = useCallback((productId: string): ProductPriceData | null => {
    return prices[productId] ?? null;
  }, [prices]);

  // Helper: obtener precio para mostrar (oferta o normal)
  const getDisplayPrice = useCallback((productId: string): number => {
    const priceData = prices[productId];
    if (!priceData) return 0;
    return priceData.isOnSale && priceData.offerPrice
      ? priceData.offerPrice
      : priceData.price;
  }, [prices]);

  // Helper: obtener precio original (si hay oferta)
  const getOriginalPrice = useCallback((productId: string): number | null => {
    const priceData = prices[productId];
    if (!priceData || !priceData.isOnSale) return null;
    return priceData.price;
  }, [prices]);

  // Helper: verificar si está en oferta
  const isOnSale = useCallback((productId: string): boolean => {
    return prices[productId]?.isOnSale ?? false;
  }, [prices]);

  // Helper: obtener porcentaje de descuento
  const getDiscountPercentage = useCallback((productId: string): number | null => {
    return prices[productId]?.discountPercentage ?? null;
  }, [prices]);

  return {
    prices,
    loading,
    error,
    refetch: fetchPrices,
    getPrice,
    getDisplayPrice,
    getOriginalPrice,
    isOnSale,
    getDiscountPercentage
  };
}

/**
 * Limpiar cache de precios (útil después de actualizaciones)
 */
export function clearPriceCache(): void {
  priceCache.clear();
}

/**
 * Obtener precio efectivo según tipo de precio del usuario
 * Función standalone para uso fuera de componentes React
 */
export async function fetchProductPricesStandalone(
  productIds: string[],
  priceType: PriceType = 'publico'
): Promise<ProductPricesMap> {
  const { data: priceRows, error } = await supabase
    .from('product_prices')
    .select('product_id, price_type, min_quantity, price, offer_price, offer_days')
    .in('product_id', productIds)
    .eq('price_type', priceType);

  if (error) throw error;

  const pricesMap: ProductPricesMap = {};

  if (priceRows && priceRows.length > 0) {
    const grouped: Record<string, typeof priceRows> = {};
    priceRows.forEach(row => {
      (grouped[row.product_id] ||= []).push(row);
    });

    Object.entries(grouped).forEach(([productId, rows]) => {
      const sortedRows = rows.sort((a, b) => a.min_quantity - b.min_quantity);
      const baseRow = sortedRows[0];

      const hasOffer = baseRow.offer_price != null && baseRow.offer_price > 0;
      const discountPercentage = hasOffer
        ? Math.round(((baseRow.price - baseRow.offer_price!) / baseRow.price) * 100)
        : null;

      pricesMap[productId] = {
        price: baseRow.price,
        offerPrice: baseRow.offer_price ?? null,
        offerDays: baseRow.offer_days ?? null,
        discountPercentage,
        isOnSale: hasOffer,
        minQuantity: baseRow.min_quantity
      };
    });
  }

  return pricesMap;
}

export default useProductPrices;
